import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import { usePublicSettings } from '../context/PublicSettingsContext';
import './Home.css';

const API_ORIGIN = '';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.35 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const collections = [
  {
    title: 'Seasonal Subscriptions',
    desc: 'Weekly fresh bouquets delivered for a full month — spring, summer, or fall',
    img: '/DSC_3838.JPG',
    to: '/shop?category=Subscriptions',
  },
  {
    title: 'Wrapped Bouquets',
    desc: 'Pick your style — Wildflower Medley, Romantic Pastel, or Sunrise Bright, in medium or large',
    img: '/DSC_3829.JPG',
    to: '/shop?category=Wrapped%20Bouquets',
  },
  {
    title: 'Arranged Bouquets',
    desc: 'Hand-designed styles by Cory — Garden Romance or Wildflower Farmhouse, in small, medium, or large',
    img: '/Blush&BerryBloom.png',
    to: '/shop?category=Arranged%20Bouquets',
  },
  {
    title: 'Dahlia Tubers',
    desc: 'Grow your own — premium dahlia tubers from our Finger Lakes farm',
    img: '/ScarletPetalSymphony.png',
    to: '/shop?category=Dahlia%20Tubers',
  },
  {
    title: 'Weddings & Events',
    desc: 'Finger Lakes weddings start here — contact Cory for consultation, custom quotes, and full-service florals',
    img: '/coryowner.png',
    to: '/contact?topic=wedding',
    cta: 'Inquire',
  },
];

const galleryImages = [
  '/ScarletPetalSymphony.png',
  '/Blush&BerryBloom.png',
  '/Wildflower_Sunrise.png',
  '/DSC_3838.JPG',
  '/DSC_3892.JPG',
  '/coryowner.png',
];

const Home = () => {
  const { settings } = usePublicSettings();
  const weddingPopup = settings?.wedding_popup;
  const homeWedding = settings?.home_wedding;
  const [showWeddingPopup, setShowWeddingPopup] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', phone: '', message: '' });
  const [leadAcceptedLegal, setLeadAcceptedLegal] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('cb_wedding_popup_seen')) return;
    if (weddingPopup && weddingPopup.enabled !== false) setShowWeddingPopup(true);
  }, [weddingPopup]);

  const dismissWeddingPopup = () => {
    sessionStorage.setItem('cb_wedding_popup_seen', '1');
    setShowWeddingPopup(false);
  };

  const submitLead = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Wedding floral inquiry — Cory Blossom');
    const body = encodeURIComponent(
      `Name: ${lead.name}\nEmail: ${lead.email}\nPhone: ${lead.phone}\n\n${lead.message || 'Wedding inquiry from homepage popup'}`
    );
    window.location.href = `mailto:coryblossom22@gmail.com?subject=${subject}&body=${body}`;
    setLeadSent(true);
    setTimeout(() => {
      dismissWeddingPopup();
    }, 1800);
  };

  const popupImg = weddingPopup?.image
    ? (weddingPopup.image.startsWith('/uploads') ? `${API_ORIGIN}${weddingPopup.image}` : weddingPopup.image)
    : '/coryowner.png';

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <motion.main
      className="home"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <motion.div className="hero__bg" style={{ y: heroY }}>
          <img
            src="/background.png"
            alt="Cory Blossom flower farm greenhouse"
          />
          <div className="hero__overlay" />
        </motion.div>

        <motion.div className="hero__content" style={{ opacity: heroOpacity }}>
          <motion.span
            className="hero__eyebrow script-font"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Welcome to
          </motion.span>

          <motion.h1
            className="hero__title"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            Cory Blossom
          </motion.h1>

          <motion.p
            className="hero__subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            Fresh flowers from the Finger Lakes, grown with love
          </motion.p>

          <motion.div
            className="hero__actions"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <Link to="/shop" className="btn btn-white">Shop Now</Link>
            <Link to="/gallery" className="btn btn-outline" style={{ borderColor: 'white', color: 'white' }}>View Gallery</Link>
          </motion.div>
        </motion.div>

        <motion.div className="hero__scroll-indicator" style={{ opacity: heroOpacity }}>
          <div className="hero__scroll-arrow">
            <span className="hero__scroll-text">Scroll</span>
            <div className="hero__scroll-line" />
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </motion.div>
      </section>

      {/* Admin-editable wedding spotlight */}
      {homeWedding && (homeWedding.title || homeWedding.text) && (
        <section className="home-wedding-spotlight">
          <div className="container">
            <div className="home-wedding-spotlight__grid">
              <div className="home-wedding-spotlight__copy">
                {homeWedding.eyebrow && <span className="script-font home-wedding-spotlight__eyebrow">{homeWedding.eyebrow}</span>}
                <h2>{homeWedding.title || 'Weddings'}</h2>
                <p>{homeWedding.text}</p>
                <Link to={homeWedding.link || '/weddings'} className="btn btn-primary">
                  {homeWedding.linkLabel || 'Explore weddings'}
                </Link>
              </div>
              <div className="home-wedding-spotlight__img">
                <img
                  src={homeWedding.image?.startsWith('/uploads') ? `${API_ORIGIN}${homeWedding.image}` : (homeWedding.image || '/coryowner.png')}
                  alt=""
                />
              </div>
            </div>
          </div>
        </section>
      )}

      <AnimatePresence>
        {showWeddingPopup && weddingPopup && (
          <motion.div
            className="home-wedding-popup__shell"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="home-wedding-popup__overlay" onClick={dismissWeddingPopup} role="presentation" />
            <motion.div
              className="home-wedding-popup"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ type: 'tween', duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
              onClick={e => e.stopPropagation()}
            >
              <button type="button" className="home-wedding-popup__close" onClick={dismissWeddingPopup} aria-label="Close">×</button>
              <div className="home-wedding-popup__inner">
                <div className="home-wedding-popup__visual">
                  <img src={popupImg} alt="" />
                </div>
                <div className="home-wedding-popup__body">
                  <h3>{weddingPopup.title || 'Planning a wedding?'}</h3>
                  <p>{weddingPopup.body}</p>
                  {!leadSent ? (
                    <form className="home-wedding-popup__form" onSubmit={submitLead}>
                      <input required placeholder="Your name" value={lead.name} onChange={e => setLead(l => ({ ...l, name: e.target.value }))} />
                      <input required type="email" placeholder="Email" value={lead.email} onChange={e => setLead(l => ({ ...l, email: e.target.value }))} />
                      <input placeholder="Phone" value={lead.phone} onChange={e => setLead(l => ({ ...l, phone: e.target.value }))} />
                      <textarea rows="2" placeholder="Tell us your date or vision (optional)" value={lead.message} onChange={e => setLead(l => ({ ...l, message: e.target.value }))} />
                      <label className="home-wedding-popup__legal">
                        <input
                          type="checkbox"
                          checked={leadAcceptedLegal}
                          onChange={(e) => setLeadAcceptedLegal(e.target.checked)}
                        />
                        <span>
                          I agree to the{' '}
                          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                          {' '}and{' '}
                          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>.
                        </span>
                      </label>
                      <button type="submit" className="btn btn-primary" disabled={!leadAcceptedLegal}>Send &amp; stay in touch</button>
                    </form>
                  ) : (
                    <p className="home-wedding-popup__thanks">Thank you — we&apos;ll be in touch!</p>
                  )}
                  <Link to={weddingPopup.ctaLink || '/weddings'} className="btn btn-outline home-wedding-popup__cta" onClick={dismissWeddingPopup}>
                    {weddingPopup.ctaLabel || 'Wedding gallery'}
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quote Section */}
      <section className="quote-section">
        <div className="container">
          <AnimatedSection>
            <div className="quote-divider">
              <span className="quote-leaf">&#x1F33F;</span>
            </div>
            <blockquote className="quote">
              <span className="script-font">"Happiness is to hold flowers in both hands."</span>
            </blockquote>
          </AnimatedSection>
        </div>
      </section>

      {/* Collections Section */}
      <section className="collections">
        <div className="container">
          <AnimatedSection>
            <h2 className="section-title">Our Collections</h2>
            <p className="section-subtitle">Handpicked arrangements for every moment</p>
          </AnimatedSection>

          <div className="collections__grid">
            {collections.map((col, i) => (
              <AnimatedSection key={col.title} delay={Math.min(i * 0.08, 0.4)} direction="up">
                <Link to={col.to} className="collection-card">
                  <div className="collection-card__img-wrapper">
                    <img
                      src={col.img}
                      alt={col.title}
                    />
                    <div className="collection-card__overlay" />
                  </div>
                  <div className="collection-card__content">
                    <h3>{col.title}</h3>
                    <p>{col.desc}</p>
                    <span className="collection-card__cta">
                      {col.cta || 'Shop Now'}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Banner */}
      <section className="feature-banner">
        <div className="feature-banner__bg">
          <img
            src="/background.png"
            alt="Cory Blossom flower farm greenhouse"
          />
          <div className="feature-banner__overlay" />
        </div>
        <div className="container feature-banner__content">
          <AnimatedSection direction="left">
            <span className="script-font feature-banner__eyebrow">From the Finger Lakes</span>
            <h2>Grown in New York's<br />Finger Lakes Region</h2>
            <p>Nestled among rolling hills and pristine lakes, our farm brings you handcrafted arrangements with freshly picked flowers from the heart of the Finger Lakes.</p>
            <Link to="/about" className="btn btn-white">Our Story</Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="gallery-preview">
        <div className="container">
          <AnimatedSection>
            <h2 className="section-title">Bloom Gallery</h2>
            <p className="section-subtitle">Moments captured in petals</p>
          </AnimatedSection>

          <div className="gallery-preview__grid">
            {galleryImages.map((img, i) => (
              <AnimatedSection
                key={i}
                delay={Math.min(i * 0.05, 0.35)}
                direction="up"
                className="gallery-preview__item"
              >
                <div className="gallery-preview__img-wrapper">
                  <img src={img} alt={`Gallery ${i + 1}`} loading="lazy" />
                  <div className="gallery-preview__img-overlay">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                    </svg>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/gallery" className="btn btn-outline">View Full Gallery</Link>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <AnimatedSection className="cta-content">
            <span className="script-font cta-eyebrow">Ready to bloom?</span>
            <h2>Let's Create Something Beautiful</h2>
            <p>Whether it's a Finger Lakes vineyard wedding, a lakeside event, or just because — we'd love to bring your floral vision to life.</p>
            <div className="cta-actions">
              <Link to="/contact" className="btn btn-primary">Get in Touch</Link>
              <Link to="/shop" className="btn btn-outline">Browse Shop</Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </motion.main>
  );
};

export default Home;
